import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { QueryMetricsResponse } from '@vendasta/multi-location-analytics';
import { TaxonomyOption, TaxonomyService } from '@vendasta/taxonomy';
import { Observable, combineLatest, forkJoin, of } from 'rxjs';
import { filter, map, shareReplay, switchMap } from 'rxjs/operators';
import { AccountGroup } from '../account-group';
import { LocationsService } from '../locations';
import { Row, rowify } from './helpers';
import { QueryService } from './query.service';

class TaxonomyOptionWithCount {
  constructor(public taxonomyOption: TaxonomyOption, public numLocations: number) {}
}

class GeoWithCount {
  constructor(
    public countryCode: string,
    public country: string,
    public state: string,
    public stateCode: string,
    public city: string,
    public numLocations: number,
  ) {}
}

class Country {
  constructor(public countryCode: string, public countryName: string, public stateCodeToName: Map<string, string>) {}

  stateName(stateCode: string): string {
    const val = this.stateCodeToName.get(stateCode);
    if (!val) {
      return stateCode;
    }
    return val;
  }
}

class CountryDetails {
  constructor(public countryCodeToCountry: Map<string, Country>) {}

  public countryName(countryCode: string): string {
    const val = this.countryCodeToCountry.get(countryCode);
    if (!val) {
      return countryCode;
    }
    return val.countryName;
  }

  public stateName(countryCode: string, stateCode: string): string {
    const val = this.countryCodeToCountry.get(countryCode);
    if (!val) {
      return stateCode;
    }
    return val.stateName(stateCode);
  }
}

@Injectable({ providedIn: 'root' })
export class AccountGroupMetricService {
  readonly filteredAccountGroupIdsForPath$: Observable<Set<string>>;
  readonly filteredLocationsForPath$: Observable<{
    [key: string]: AccountGroup;
  }>;

  readonly allTaxonomyOptions$: Observable<TaxonomyOption[]>;
  private brandTaxonomyRows$: Observable<Row[]>;
  readonly brandTaxonomyOptions$: Observable<Map<string, TaxonomyOptionWithCount>>;

  constructor(
    private queryService: QueryService,
    private httpClient: HttpClient,
    private locationsService: LocationsService,
    private taxonomyService: TaxonomyService,
  ) {
    this.filteredAccountGroupIdsForPath$ = this.getAccountGroupIds$();

    this.filteredLocationsForPath$ = combineLatest([
      this.locationsService.currentAccountGroups$,
      this.filteredAccountGroupIdsForPath$,
    ]).pipe(
      map(([locations, filteredAgids]) => {
        if (locations == null || filteredAgids == null) {
          return null;
        }
        const filtered = {};
        Object.keys(locations)
          .map((k) => locations[k])
          .forEach((ag: AccountGroup) => {
            if (!filteredAgids.has(ag.accountGroupId)) {
              return;
            }
            filtered[ag.accountGroupId] = ag;
          });
        return filtered;
      }),
      shareReplay({ refCount: true, bufferSize: 1 }),
    );

    this.allTaxonomyOptions$ = this.taxonomyService
      .getTaxonomyIds()
      .pipe(shareReplay({ refCount: true, bufferSize: 1 }));
    this.brandTaxonomyRows$ = this.queryService.accountGroupTaxonomies$.pipe(
      map((r: QueryMetricsResponse) => {
        if (r == null) {
          return null;
        }
        return rowify(r.metricResults[0]);
      }),
    );
    this.brandTaxonomyOptions$ = combineLatest([this.allTaxonomyOptions$, this.brandTaxonomyRows$]).pipe(
      map(([allTax, brandRows]) => {
        if (!allTax || !brandRows) {
          return null;
        }
        const allTaxMap = new Map<string, TaxonomyOption>();
        allTax.forEach((tax) => {
          allTaxMap.set(tax.id, tax);
        });
        const brandTaxMap = new Map<string, TaxonomyOptionWithCount>();
        // TODO: Flip this so that we're only getting/inflating what we have? (probably way faster/lighter?)
        brandRows.forEach((row) => {
          // Some accounts have the same taxId multiple times, so reduce to the set before counting
          Array.from<string>(new Set(row.values[0])).forEach((taxId) => {
            const existing = brandTaxMap.get(taxId);
            if (existing) {
              // Update count
              existing.numLocations += +row.measures[0];
              brandTaxMap.set(taxId, existing);
            } else {
              // Get tax and set count
              brandTaxMap.set(taxId, new TaxonomyOptionWithCount(allTaxMap.get(taxId), +row.measures[0]));
            }
          });
        });
        return brandTaxMap;
      }),
      shareReplay({ refCount: true, bufferSize: 1 }),
    );
  }

  private getAccountGroupIds$(): Observable<Set<string>> {
    return this.queryService.accountGroup$.pipe(
      filter((r) => !!r),
      map((r: QueryMetricsResponse) => {
        const s = new Set<string>();
        if (r) {
          rowify(r.metricResults[0]).forEach((row) => {
            s.add(row.measures[0][0]);
          });
        }
        return s;
      }),
      shareReplay({ refCount: true, bufferSize: 1 }),
    );
  }

  public getGeographicBreakdown$(): Observable<GeoWithCount[]> {
    const rows$ = this.queryService.accountGroupGeography$.pipe(
      map((r: QueryMetricsResponse) => {
        if (r == null) {
          return null;
        }
        return rowify(r.metricResults[0]);
      }),
    );
    const countyInfo$ = rows$.pipe(
      switchMap((i) => {
        if (!i) {
          return of(null);
        }
        const countries = new Set<string>();
        i.forEach((c) => {
          if (!c.values[0]) {
            return;
          }
          countries.add(c.values[0]);
        });
        return this.getCountryAndStateInformation(Array.from(countries.keys()));
      }),
    );

    return combineLatest([rows$, countyInfo$]).pipe(
      map(([rows, countryInfo]: [Row[], CountryDetails]) => {
        if (!rows || !countryInfo) {
          return [];
        }
        return rows.map(function (dataRow: Row): GeoWithCount {
          return new GeoWithCount(
            dataRow.values[0],
            countryInfo.countryName(dataRow.values[0]),
            countryInfo.stateName(dataRow.values[0], dataRow.values[1]),
            dataRow.values[1],
            dataRow.values[2],
            Number(dataRow.measures[0][0]),
          );
        });
      }),
    );
  }

  private getCountryAndStateInformation(countries: string[]): Observable<CountryDetails> {
    return forkJoin(
      countries.map((c) => {
        return this.httpClient
          .get('https://www.gstatic.com/chrome/autofill/libaddressinput/chromium-i18n/ssl-address/data/' + c)
          .pipe(
            map((r) => {
              const subKeys = String(r['sub_keys']).split('~');
              const subNames = String(r['sub_names']).split('~');

              const states = new Map<string, string>();
              subKeys.forEach(function (key, i): void {
                states.set(key, subNames[i]);
              });

              return new Country(String(r['key']), capitalizeFirstLetters(String(r['name'])), states);
            }),
          );
      }),
    ).pipe(
      map((r) => {
        const m = new Map<string, Country>();
        r.forEach((c) => {
          m.set(c.countryCode, c);
        });
        return new CountryDetails(m);
      }),
    );
  }
}

function capitalizeFirstLetters(a: string): string {
  return a
    .split(' ')
    .map((c) => c.charAt(0).toUpperCase() + c.slice(1).toLowerCase())
    .join(' ');
}
